import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Flag, Eye, Trash2, RotateCcw, Loader2, Fish, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import CatchImage from './CatchImage';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFlaggedCatches({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/league-flagged-catches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data || []);
    } catch {
      toast.error('Failed to load flagged catches');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const restore = async (id) => {
    try {
      await axios.post(`${API}/admin/league-catches/${id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Catch restored');
      refresh();
    } catch {
      toast.error('Failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Permanently hide this catch from the leaderboard?')) return;
    try {
      await axios.post(`${API}/admin/league-catches/${id}/remove`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Catch removed');
      refresh();
    } catch {
      toast.error('Failed');
    }
  };

  return (
    <div className="glass-card p-6 mt-8" data-testid="admin-flagged-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white flex items-center gap-2">
          <Flag className="w-5 h-5 text-red-400" />
          Flagged League Catches ({items.length})
        </h3>
        <Button variant="ghost" size="sm" onClick={refresh} data-testid="admin-flagged-refresh">
          Refresh
        </Button>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-[#c8b273] mx-auto" />
      ) : items.length === 0 ? (
        <p className="text-[#6b7d6b] text-sm text-center py-6">No flagged catches.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((c) => (
            <li
              key={c.id}
              className={`p-3 rounded-lg flex gap-3 ${c.hidden ? 'bg-red-500/10 border border-red-500/30' : 'bg-[#141714]'}`}
              data-testid={`admin-flagged-${c.id}`}
            >
              <CatchImage row={c} aspectRatio="auto" rounded className="w-16 h-16 flex-shrink-0 rounded" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white font-medium">{c.username}</p>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-400">
                    {c.flags_count} flag{c.flags_count !== 1 ? 's' : ''}
                  </span>
                  {c.hidden && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#4a504a]/30 text-[#8a948a]">HIDDEN</span>
                  )}
                </div>
                <p className="text-[#c8b273] text-sm font-bold">{c.weight}lb {c.fish_type}</p>
                <p className="text-[#6b7d6b] text-xs flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {c.lake_name}
                </p>
                <p className="text-[#6b7d6b] text-xs">+{c.points} pts • {new Date(c.submitted_at).toLocaleString()}</p>
                {c.notes && <p className="text-[#8a948a] text-xs italic mt-1">"{c.notes}"</p>}
              </div>
              <div className="flex flex-col gap-2">
                {c.hidden ? (
                  <Button size="sm" variant="ghost" onClick={() => restore(c.id)} data-testid={`admin-restore-${c.id}`}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)} className="text-red-400" data-testid={`admin-remove-${c.id}`}>
                    <Trash2 className="w-3 h-3 mr-1" /> Hide
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
